import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { getBrands } from '@/api-clients/get-brands';
import { getEnabledChannels } from '@/api-clients/get-enabled-channels';
import { getLocations } from '@/api-clients/get-locations';
import { getPositions } from '@/api-clients/get-positions';
import {
  CAMPAIGN_STATUS_VALUES,
  useCampaignStatusLabels,
} from '@/components/base/channel-status-pill';
import { useTableContext } from '@/contexts/table-context';
import { useFilterQuery } from '@/hooks/use-filter-query';
import { queryKeys } from '@/query-keys';

/**
 * Filter configs for the campaigns page's filter bar: Channels, Position,
 * Status, Location, Brand (Duration/Search are wired separately — see
 * `filters/index.tsx`). Unlike the dashboard's equivalent hook, positions/
 * locations/brands are NOT scoped to `isSelectedForSourcing` — campaigns can
 * exist for any opening, not just ones currently selected for sourcing.
 */
export function useCampaignFilters() {
  const intl = useIntl();
  const statusLabels = useCampaignStatusLabels();

  const {
    filters: { channelIds, positionIds, locationIds, brandIds, statuses },
  } = useTableContext();

  const {
    data: channelsData,
    isFetching: isFetchingChannels,
    query: channelsQuery,
    setQuery: setChannelsQuery,
  } = useFilterQuery({
    queryKey: queryKeys.filters.channels,
    fetchFn: getEnabledChannels,
  });

  const {
    data: positionsData,
    isFetching: isFetchingPositions,
    query: positionsQuery,
    setQuery: setPositionsQuery,
  } = useFilterQuery({
    queryKey: queryKeys.filters.positions,
    fetchFn: getPositions,
  });

  const {
    data: locationsData,
    isFetching: isFetchingLocations,
    query: locationsQuery,
    setQuery: setLocationsQuery,
  } = useFilterQuery({
    queryKey: queryKeys.filters.locations,
    fetchFn: getLocations,
  });

  const {
    data: brandsData,
    isFetching: isFetchingBrands,
    query: brandsQuery,
    setQuery: setBrandsQuery,
  } = useFilterQuery({
    queryKey: queryKeys.filters.brands,
    fetchFn: getBrands,
  });

  const channelsOptions = channelsData?.channels?.map(
    ({ external_id, name }) => ({
      label: name,
      value: external_id,
    }),
  );

  const statusOptions = CAMPAIGN_STATUS_VALUES.map(status => ({
    label: statusLabels[status],
    value: status,
  }));

  const brandsOptions = brandsData?.brands?.map(({ id, name }) => ({
    label: name,
    value: id,
  }));

  const filters = useMemo(
    () => [
      {
        filterKey: 'channelIds',
        label: intl.formatMessage({
          defaultMessage: 'Channels',
          description: 'Campaigns filter: filter by sourcing channel',
        }),
        isFetching: isFetchingChannels,
        query: channelsQuery,
        setQuery: setChannelsQuery,
        options: channelsOptions,
        selectedValues: channelIds,
      },
      {
        filterKey: 'statuses',
        label: intl.formatMessage({
          defaultMessage: 'Status',
          description: 'Campaigns filter: filter by campaign status',
        }),
        options: statusOptions,
        selectedValues: statuses,
      },
      {
        filterKey: 'positionIds',
        label: intl.formatMessage({
          defaultMessage: 'Position',
          description: 'Campaigns filter: job title / position',
        }),
        isFetching: isFetchingPositions,
        query: positionsQuery,
        setQuery: setPositionsQuery,
        options: positionsData?.positions?.map(({ id, title }) => ({
          label: title,
          value: id,
        })),
        selectedValues: positionIds,
      },
      {
        filterKey: 'locationIds',
        label: intl.formatMessage({
          defaultMessage: 'Location',
          description: 'Campaigns filter: work site or location',
        }),
        isFetching: isFetchingLocations,
        query: locationsQuery,
        setQuery: setLocationsQuery,
        options: locationsData?.locations?.map(({ id, name }) => ({
          label: name,
          value: id,
        })),
        selectedValues: locationIds,
      },
      {
        filterKey: 'brandIds',
        label: intl.formatMessage({
          defaultMessage: 'Brand',
          description: 'Campaigns filter: filter by employer brand name',
        }),
        isFetching: isFetchingBrands,
        query: brandsQuery,
        setQuery: setBrandsQuery,
        options: brandsOptions,
        isMultiSelect: true,
        selectedValues: brandIds,
      },
    ],
    [
      intl,
      isFetchingChannels,
      channelsQuery,
      setChannelsQuery,
      channelsOptions,
      channelIds,
      isFetchingPositions,
      positionsQuery,
      setPositionsQuery,
      positionsData?.positions,
      positionIds,
      statusOptions,
      statuses,
      isFetchingLocations,
      locationsQuery,
      setLocationsQuery,
      locationsData?.locations,
      locationIds,
      isFetchingBrands,
      brandsQuery,
      setBrandsQuery,
      brandsOptions,
      brandIds,
    ],
  );

  return filters;
}
